import { Options } from "@tenforce/product-fields"
import {
    getSelectedChildren,
    getSelectedValues,
} from "../apis/treeSelector/treeSelector"
import { Type } from "../apis/models/treeSelector/models"
import { typeHasGroups } from "../apis/treeSelector/utils"
import { SelectableValue } from "../apis/treeSelector/types"
import {
    isFieldItemSelect,
    mapToGroupedTreeStructure,
} from "../components/fields/components/treeSelector/utils"
import {
    getSelectableFilterGroupOptions,
    getSelectableFilterOptions,
} from "../apis/collections/selectorFilter"

type UseHierarchicalSelectableDataProps = {
    pageSize?: number
    itemId: number
    fieldId: string
}

type IHook = {
    fetchOptions: (
        filter: string,
        id: string,
        type: Type,
        skip: number,
        selectedIds: string[],
    ) => Promise<Options<SelectableValue>>
    fetchSelectedOptions: (
        filter: string,
        selectedIds: string[],
        type: Type,
    ) => Promise<
        Omit<Options<SelectableValue>, "resultCount" | "isGroupAsValue">
    >
}

export const useHierarchicalSelectableData = ({
    pageSize = 20,
    itemId,
    fieldId,
}: UseHierarchicalSelectableDataProps): IHook => {
    const fetchOptions = async (
        filterValue: string,
        activeFieldId: string,
        type: Type,
        skip: number,
        selectedIds: string[],
    ): Promise<Options<SelectableValue>> => {
        try {
            if (typeHasGroups(type)) {
                const valueResult = await getSelectableFilterGroupOptions(
                    itemId,
                    activeFieldId,
                    fieldId,
                    filterValue,
                    skip,
                    pageSize,
                    type,
                )

                const decoratedData = mapToGroupedTreeStructure(
                    valueResult.data || [],
                    type,
                    selectedIds,
                )
                return {
                    result: decoratedData,
                    totalCount: valueResult.totalChildrenCount,
                    resultCount: valueResult.data.length,
                    isGroupAsValue: isFieldItemSelect(type),
                }
            } else {
                const result = await getSelectableFilterOptions(
                    itemId,
                    activeFieldId,
                    fieldId,
                    filterValue,
                    skip,
                    pageSize,
                    type,
                )

                const treeStructureResult = mapToGroupedTreeStructure(
                    result.data || [],
                    type,
                    selectedIds,
                )

                return {
                    result: treeStructureResult,
                    totalCount: result.totalChildrenCount,
                    resultCount: treeStructureResult.length,
                    isGroupAsValue: isFieldItemSelect(type),
                }
            }
        } catch (error) {
            console.error("Error fetching options:", error)
            throw error
        }
    }

    const fetchSelectedOptions = async (
        filterValue: string,
        selectedIds: string[],
        type: Type,
    ): Promise<
        Omit<Options<SelectableValue>, "resultCount" | "isGroupAsValue">
    > => {
        try {
            if (typeHasGroups(type)) {
                const valueResult = await getSelectedChildren(
                    type,
                    filterValue,
                    selectedIds,
                )

                const decoratedData = mapToGroupedTreeStructure(
                    valueResult.data || [],
                    type,
                    selectedIds,
                )

                return {
                    result: decoratedData,
                    totalCount: valueResult.totalChildrenCount,
                }
            } else {
                const result = await getSelectedValues(
                    type,
                    filterValue,
                    selectedIds,
                )

                const treeStructureResult = mapToGroupedTreeStructure(
                    result.data || [],
                    type,
                    selectedIds,
                )

                return {
                    result: treeStructureResult,
                    totalCount: result.totalChildrenCount,
                }
            }
        } catch (error) {
            console.error("Error fetching selected options:", error)
            throw error
        }
    }

    return {
        fetchOptions,
        fetchSelectedOptions,
    }
}
